export const getUTMData = () => {
    if (typeof window === "undefined") {
        return {
            LeadSource: "Direct / Website",
            ReferrerName: "",
            utm_source: "",
            utm_medium: "",
            lead_url_string: "",
        };
    }

    const searchParams = new URLSearchParams(window.location.search);

    // Prefer URL params, fall back to sessionStorage (persistent for session)
    let utm_source = searchParams.get("utm_source") || (sessionStorage.getItem("l2r_utm_source") || null);
    let utm_medium = searchParams.get("utm_medium") || (sessionStorage.getItem("l2r_utm_medium") || null);
    let lead_url_string = window.location.href || (sessionStorage.getItem("l2r_lead_url_string") || "");

    // NOTE: Persist UTMs on landing so attribution survives navigation within the session.
    // Stored keys: l2r_utm_source, l2r_utm_medium, l2r_lead_url_string, l2r_lead_source, l2r_referrer_name

    let LeadSource = "Direct / Website";

    if (utm_medium) {
        const medium = utm_medium.toLowerCase();
        if (medium.includes("cpc") || medium.includes("paid")) {
            LeadSource = "Paid Social";
        } else if (medium.includes("influencer")) {
            LeadSource = "Influencer";
        } else if (medium.includes("organic")) {
            LeadSource = "Organic Social";
        } else if (medium.includes("referral")) {
            LeadSource = "Referral";
        } else if (medium.includes("email")) {
            LeadSource = "Email Marketing";
        }
    }

    // If utm_medium wasn't present in the URL but we persisted a LeadSource earlier,
    // prefer the persisted LeadSource so attribution remains consistent across navigation.
    try {
        const persistedLead = sessionStorage.getItem("l2r_lead_source");
        if ((!utm_medium || utm_medium === null || utm_medium === "") && persistedLead) {
            LeadSource = persistedLead;
        }
    } catch (e) {
        // ignore storage errors
    }

    return {
        LeadSource,
        ReferrerName: utm_source || "", // "Notes" or "Referrer Name"
        utm_source: utm_source || "",
        utm_medium: utm_medium || "",
        lead_url_string,
        // API-facing normalized lead source code (use these values when sending to backend)
        LeadSourceCode: (() => {
            try {
                const map: Record<string, string> = {
                    "Paid Social": "Paid_Social",
                    "Organic Social": "Organic_Social",
                    "Influencer": "Influencer",
                    "Referral": "Customer_Referral",
                    "Email Marketing": "Email_Marketing",
                    "Paid Search": "Paid_Search",
                    "Organic Search": "Organic_Search",
                    "Direct / Website": "Direct",
                };
                return map[LeadSource] || LeadSource.replace(/\s+/g, "_") || "Direct";
            } catch (e) {
                return LeadSource.replace(/\s+/g, "_") || "Direct";
            }
        })(),
    };
};

export const triggerGenerateLead = () => {
    if (typeof window === "undefined") return;
    const { LeadSource, ReferrerName, utm_source, utm_medium, lead_url_string } = getUTMData();

    const payload = {
        currency: "USD",
        value: 0,
        lead_source: LeadSource,
        // Explicit debug-friendly duplicate so it's easy to find in network payloads
        ep_lead_source: LeadSource,
        referrer_name: ReferrerName,
        utm_source,
        utm_medium,
        lead_url_string,
    };

    // Always show the payload in dev for debugging, even if gtag isn't ready
    try {
        if (process.env.NODE_ENV !== "production") {
            // eslint-disable-next-line no-console
            console.debug("GA generate_lead payload:", payload);
        }
    } catch (e) {
        // ignore
    }

    // If gtag is available, send immediately; otherwise queue the event so it can be flushed later.
    if ((window as any).gtag) {
        (window as any).gtag("event", "generate_lead", payload);
        // If there were queued events, flush them now as well
        try {
            const q = (window as any).__l2r_queued_events || [];
            if (Array.isArray(q) && q.length > 0) {
                q.forEach((ev: any) => {
                    (window as any).gtag("event", ev.name || "generate_lead", ev.payload || ev);
                });
                (window as any).__l2r_queued_events = [];
            }
        } catch (e) {
            // ignore
        }
        // Clear persisted UTMs after sending lead event (we assume triggerGenerateLead is
        // called after a successful API lead submission).
        try {
            sessionStorage.removeItem("l2r_utm_source");
            sessionStorage.removeItem("l2r_utm_medium");
            sessionStorage.removeItem("l2r_lead_url_string");
            sessionStorage.removeItem("l2r_lead_source");
            sessionStorage.removeItem("l2r_referrer_name");
        } catch (e) {
            // ignore
        }
    } else {
        try {
            (window as any).__l2r_queued_events = (window as any).__l2r_queued_events || [];
            (window as any).__l2r_queued_events.push({ name: "generate_lead", payload });
            // eslint-disable-next-line no-console
            console.warn("gtag not available yet — queued generate_lead event for later.", payload);
        } catch (e) {
            // ignore
        }
    }
};

// Fire a lightweight event on initial page load when UTM params exist in the URL.
// This does not persist UTMs anywhere — it only notifies GA that the session
// arrived with UTMs so you can validate attribution immediately.
export const captureUTMOnLoad = () => {
    if (typeof window === "undefined") return;
    const { utm_source, utm_medium, lead_url_string } = getUTMData();
    if ((utm_source && utm_source.length > 0) || (utm_medium && utm_medium.length > 0)) {
        if ((window as any).gtag) {
            try {
                try {
                    if (process.env.NODE_ENV !== "production") {
                        // eslint-disable-next-line no-console
                        console.debug("GA utm_capture payload:", { utm_source, utm_medium, lead_url_string });
                    }
                } catch (e) {
                    // ignore
                }
                (window as any).gtag("event", "utm_capture", {
                    utm_source,
                    utm_medium,
                    lead_url_string,
                });
                // Persist UTM info for the session so subsequent navigation keeps attribution
                try {
                    sessionStorage.setItem("l2r_utm_source", utm_source || "");
                    sessionStorage.setItem("l2r_utm_medium", utm_medium || "");
                    sessionStorage.setItem("l2r_lead_url_string", lead_url_string || "");
                    // compute LeadSource for storage as well
                    try {
                        const medium = (utm_medium || "").toLowerCase();
                        let storedLead = "Direct / Website";
                        if (medium.includes("cpc") || medium.includes("paid")) storedLead = "Paid Social";
                        else if (medium.includes("influencer")) storedLead = "Influencer";
                        else if (medium.includes("organic")) storedLead = "Organic Social";
                        else if (medium.includes("referral")) storedLead = "Referral";
                        else if (medium.includes("email")) storedLead = "Email Marketing";
                        sessionStorage.setItem("l2r_lead_source", storedLead);
                        sessionStorage.setItem("l2r_referrer_name", utm_source || "");
                    } catch (e) {
                        // ignore
                    }
                } catch (e) {
                    // ignore storage errors
                }
                // If there were queued events and gtag is now available, flush them
                try {
                    const q = (window as any).__l2r_queued_events || [];
                    if (Array.isArray(q) && q.length > 0) {
                        q.forEach((ev: any) => {
                            (window as any).gtag("event", ev.name || "generate_lead", ev.payload || ev);
                        });
                        (window as any).__l2r_queued_events = [];
                    }
                } catch (e) {
                    // ignore
                }
            } catch (e) {
                // ignore
            }
        }
    }
};
